import { useEffect } from 'react';
import { Customer, GcpPlacementService, Site } from '../../../../../types';
import { useUploadStore } from '../../../../../store/uploadStore';
import { parseGcpCsvUrl } from './gcpRequestUploadFiles/parseGcpCsv';
import { recalcGcpStats } from './gcpRequestUploadFiles/useHandleGcpFileUpload';

export const createGcpRequestUploadState = (gcpPlacementService?: GcpPlacementService) => {
  const measurements = gcpPlacementService ? gcpPlacementService.flightMarkerData.map(({ url }) => url) : [];
  const photos = gcpPlacementService ? gcpPlacementService.flightMarkerImages.map(({ url }) => url) : [];
  const comment = gcpPlacementService?.commentFromPilotSurveyor ?? '';
  const gcpServiceLaborTimeInHours =
    gcpPlacementService?.gcpServiceLaborTimeInHours != null
      ? Math.ceil(gcpPlacementService.gcpServiceLaborTimeInHours / 0.25) * 0.25
      : undefined;

  return {
    airtableRecordId: gcpPlacementService ? gcpPlacementService.airtableRecordId : null,
    measurements,
    photos,
    comment,
    gcpServiceLaborTimeInHours,
    measurementUploads: [],
    photoUploads: [],
    meta: {
      isEditMode: !gcpPlacementService?.flightMarkerData.length,
      isSubmitting: false,
      prevMeasurements: measurements.concat(),
      prevPhotos: photos.concat(),
      prevComment: comment,
      prevGcpServiceLaborTimeInHours: gcpServiceLaborTimeInHours,
      prevMeasurementUploads: [],
      prevPhotoUploads: [],
    },
  };
};

export const useCreateOrRestoreGcpRequestState = (
  customer: Customer,
  site: Site,
  gcpPlacementService?: GcpPlacementService,
) => {
  const { getGcpRequest, setGcpRequest } = useUploadStore();

  useEffect(() => {
    let gcpRequest = getGcpRequest(customer.id, site.id);

    if (!gcpRequest) {
      gcpRequest = createGcpRequestUploadState(gcpPlacementService);

      setGcpRequest(customer.id, site.id, gcpRequest);
    }

    // Backend-saved measurement files never go through the upload flow (and its parsing),
    // so the "GCPs measured" count is otherwise never computed for a restored request. Only
    // bother fetching/parsing them once GCP service hours are actually set — an empty/unset
    // value means the request isn't at a stage where the count is shown, so skip the requests.
    if (
      gcpRequest.restoredMeasurementParseResults === undefined &&
      gcpRequest.measurements.length > 0 &&
      gcpRequest.gcpServiceLaborTimeInHours != null
    ) {
      const request = gcpRequest;
      Promise.all(request.measurements.map((url) => parseGcpCsvUrl(url))).then((results) => {
        // Bail if the store entry was replaced/removed while the fetch was in flight.
        if (getGcpRequest(customer.id, site.id) !== request) return;
        request.restoredMeasurementParseResults = results;
        recalcGcpStats(request);
        setGcpRequest(customer.id, site.id, request);
      });
    }
  }, [gcpPlacementService]);
};
